import { I18n } from "@assets/strings";
import React, { useMemo, useState } from "react";
import { ClipLoader } from "react-spinners";
import styled, { css } from "styled-components";
import { useServices } from "@core/servicesContext";
import { useOrganizationAlias } from "@domain/currentProfile/hooks";
import { ImageVersions } from "@domain/media/dtos";
import { useOrganizationAssets } from "@domain/organization/hooks";
import { Organization } from "@domain/organization/organization";
import { useIsMobile } from "@utils/hooks";
import { RoundedPlusButton } from "@ui/components/buttons/roundedPlusButton";
import { Description } from "@ui/components/containers/description";
import { PrimaryBold, PrimaryMedium } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { DeleteIcon, SendIcon } from "@ui/components/icons";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { Row, Stack, row } from "@ui/components/layout";
import { ImageView } from "@ui/components/media/imageView";
import { FileUpload } from "@ui/components/mediaEditor/fileUpload";
import { fadeIn } from "@ui/styles/keyframes";
import { desktop, mobile } from "@ui/styles/utils";
import { Z } from "@ui/styles/zIndex";
import { ShareroomBlocIcons } from "@ui/pages/group/shareroom/shareroomInfo";

const MAX_PHOTOS_COUNT = 9;
interface OrganizationAssetsProps extends React.HTMLAttributes<HTMLDivElement> {
	organization: Organization;
}
export const OrganizationAssets: React.FC<OrganizationAssetsProps> = ({ organization, ...htmlProps }) => {
	const { organizationService, mediaService } = useServices();
	const { result: assets, loading: assetsLoading, fetch } = useOrganizationAssets(organization.id);
	const isMobile = useIsMobile();
	const defaultCount = isMobile ? 2 : 3;
	const [showAllPictures, setShowAllPictures] = useState(assets.length < defaultCount);
	const [edit, setEdit] = useState<string | undefined>("");
	const [displayName, setDisplayName] = useState<string>("");
	const displayedAssets = useMemo(
		() => (showAllPictures ? assets : assets.slice(0, defaultCount)),
		[showAllPictures, assets]
	);
	const alias = useOrganizationAlias();

	// if (!alias && assets.length === 0) {
	// 	return null;
	// }
	const changeDisplayName = (id: string): void => {
		mediaService
			.edit(id, displayName)
			.then(() => {
				fetch();
				setDisplayName("");
				setEdit("");
			})
			.catch((error) => {
				console.log(error);
			});
	};

	return (
		<Container {...htmlProps}>
			<SubtitleContainer>
				{/* <span>
					{I18n.t("organization.pictures.title")} {alias ? `(${assets.length}/${MAX_PHOTOS_COUNT})` : ""}
				</span> */}
				{/* <Subtitle> */}
				<StyledPrimarybold>
					{I18n.t("organization.pictures.title")} {alias ? `(${assets.length}/${MAX_PHOTOS_COUNT})` : ""}
				</StyledPrimarybold>
				{/* {!!alias && (
						<UploadImageContainer onUpload={(media) => organizationService.addAsset(organization.id, media)}>
							{(_, loading) => <RoundedPlusButton loading={loading} />}
						</UploadImageContainer>
					)} */}
				{/* </Subtitle> */}
				<Row gap={8} justify="center" align="center">
					{assets.length > defaultCount && (
						<Fold>
							<span onClick={() => setShowAllPictures(!showAllPictures)}>
								{showAllPictures ? I18n.t("see_less") : I18n.t("see_more")}
							</span>
						</Fold>
					)}
					{!!alias && (
						<UploadImageContainer
							multiple
							onMultipleUpload={(media) => organizationService.addAsset(organization.id, media)}
						>
							{(_, loading) => <RoundedPlusButton loading={loading} />}
						</UploadImageContainer>
					)}
				</Row>
			</SubtitleContainer>
			{assets.length === 0 ? (
				<Stack style={{ width: "100%" }} gap={10} align="center" justify="center">
					<IconView>
						<img src={ShareroomBlocIcons["presentation"]} />
					</IconView>

					<Description>
						{I18n.t(`organization.pictures.${"presentation"}.${alias ? "owner" : "member"}.description`)}
					</Description>
				</Stack>
			) : (
				<Content>
					{assetsLoading ? (
						<ClipLoader />
					) : (
						// assets.length === 0 ? (
						// 	!!alias && (
						// 		<UploadImageContainer onUpload={(media) => organizationService.addAsset(organization.id, media)}>
						// 			{(_, loading) => <LoadingButton loading={loading}>{I18n.t("organization.pictures.add")}</LoadingButton>}
						// 		</UploadImageContainer>
						// 	)
						// ) :
						displayedAssets.map((asset) => (
							<DisplayedContainer key={asset.id}>
								<Picture>
									<ImageView image={asset} imageVersion={ImageVersions.MEDIUM} gallery={assets} />
									{!!alias && (
										<DeleteImageIcon onClick={() => organizationService.deleteAsset(organization.id, asset.id)}>
											<DeleteIcon />
										</DeleteImageIcon>
									)}
								</Picture>
								{!!alias && edit === asset.id ? (
									<DisplayName>
										<SimpleTextInput value={displayName ? displayName : ""} onChange={(v) => setDisplayName(v)} />
										<SendIconContainer onClick={() => changeDisplayName(asset.id)} disabled={false}>
											<SendIcon />
										</SendIconContainer>
									</DisplayName>
								) : (
									<DisplayName>
										<PrimaryBold
											onClick={() => {
												if (alias) {
													setEdit(asset.id);
													setDisplayName(asset.displayName);
												}
											}}
										>
											{asset.displayName}
										</PrimaryBold>
									</DisplayName>
								)}
							</DisplayedContainer>
						))
					)}
				</Content>
			)}
		</Container>
	);
};

const Container = styled(RoundedBlock)`
	padding: 30px 40px;
`;

const SubtitleContainer = styled.div`
	${row(10, "space-between", "center")};
	margin-bottom: 20px;
`;

const IconView = styled.div`
	${row(0, "center", "center")}
	width: 100%;
	height: 100%;
	> img {
		@media ${desktop} {
			width: 347px;
			height: 217px;
		}
	}
`;

// const Subtitle = styled.div`
// 	${row(10, "flex-start", "center")};
// 	> span {
// 		${({ theme }) => theme.textStyles.subtitle};
// 		color: ${({ theme }) => theme.colors.secondary};
// 	}
// 	> svg {
// 		display: block;
// 		cursor: pointer;
// 		path {
// 			fill: ${({ theme }) => theme.colors.secondary};
// 		}
// 	}

// 	@media ${mobile} {
// 		> span {
// 			flex: 0 1;
// 		}
// 	}
// `;

const Content = styled.div`
	max-width: 100%;
	margin: 0 auto;
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	grid-gap: 45px;

	@media ${mobile} {
		grid-template-columns: repeat(1, 1fr);
		grid-gap: 15px;
	}
`;

const UploadImageContainer = styled(FileUpload)`
	:active {
		opacity: 0.6;
	}
`;

const Picture = styled(RoundedBlock)<{ url?: string }>`
	position: relative;
	height: 286px;
	overflow: hidden;
	${row(0, "center", "center")};
	border: none;
	margin-bottom: 15px;
`;

const DisplayedContainer = styled.div`
	${row(10, "space-between", "center")};
	flex-direction: column;
`;

const DisplayName = styled.div`
	${row(10, "space-between", "center")};
`;

const DeleteImageIcon = styled.div`
	cursor: pointer;
	position: absolute;
	right: 5px;
	top: 5px;
	z-index: ${Z.above(Z.toolbar)};

	animation: ${fadeIn} 0.2s linear;
	transition: transform 0.2s ease-in-out;
	&:hover {
		transform: rotate(90deg);
	}
`;

const Fold = styled(PrimaryMedium)`
	text-align: end;
	color: ${({ theme }) => theme.colors.secondary};
	> span {
		cursor: pointer;
	}
`;

const StyledPrimarybold = styled(PrimaryBold)`
	color: ${({ theme }) => theme.colors.secondary};
	font-size: 1.5rem;
	font-weight: 800;

	@media ${mobile} {
		font-size: 0.813rem;
	}
`;

const SendIconContainer = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled: boolean }>`
	cursor: pointer;
	transition: opacity 0.1s linear;
	${row(0, "center", "center")};
	width: 30px;
	height: 30px;
	border-radius: 15px;
	background-color: ${({ theme }) => theme.colors.secondary};
	${({ disabled }) =>
		disabled &&
		css`
			cursor: initial;
			opacity: 0.6;
		`}
	> svg {
		path {
			fill: ${({ theme }) => theme.colors.white};
		}
	}
`;
